import axios from "axios";

// Wallet availability for the current host, resolved by the backend from the
// paypoint's payment-method-domain list and Apple Pay registrations. The
// component never receives the full domain list, other hosts or any paypoint
// id: it only learns, for the one host it runs on, whether each wallet may be
// shown (and the Apple Pay merchant id). Contract: docs/contracts/pin-4732.md.

export const NO_WALLET_AVAILABILITY = Object.freeze({
  applePay: Object.freeze({ enabled: false, merchantId: "" }),
  googlePay: Object.freeze({ enabled: false }),
});

// Strict normalisation: anything other than a literal `true` is "not enabled",
// so a malformed response fails closed (wallets hidden).
export function normalizeWalletAvailability(data) {
  const body = data ?? {};
  return {
    applePay: {
      enabled: body.applePay?.enabled === true,
      merchantId:
        typeof body.applePay?.merchantId === "string"
          ? body.applePay.merchantId
          : "",
    },
    googlePay: {
      enabled: body.googlePay?.enabled === true,
    },
  };
}

// Temporary tokens are single-use: the call spends the current token and the
// backend hands back a fresh one as `pageIdentifier` in the response body, which
// must replace it. Also returned on error responses issued after auth.
function rotatedTokenFrom(response) {
  const token = response?.data?.pageIdentifier;
  return typeof token === "string" && token ? token : undefined;
}

// `GET Wallet/{entry}/availability?host={host}` authenticated with the current
// temporary token. `getToken` is read at call time and `setToken` receives the
// rotated token, so the single token chain (init -> availability -> session ->
// payment) never goes stale.
// Never rejects: a failed call only hides the wallets, it must not break the
// card / ACH flows that share this component tree.
export async function fetchWalletAvailability({
  urlApi = process.env.REACT_APP_URL_API,
  entry,
  host,
  getToken,
  setToken,
  get = axios.get,
} = {}) {
  const token = typeof getToken === "function" ? getToken() : undefined;
  if (!entry || !host || !token) return NO_WALLET_AVAILABILITY;
  const rotate = (response) => {
    const next = rotatedTokenFrom(response);
    if (next && typeof setToken === "function") setToken(next);
  };
  try {
    const endpoint = `${urlApi}Wallet/${encodeURIComponent(
      entry
    )}/availability?host=${encodeURIComponent(host)}`;
    const response = await get(endpoint, {
      headers: { requestToken: token },
    });
    // The call spent the token: without the rotated one the buttons would
    // fail on the first payment, so fail closed and hide the wallets.
    if (!rotatedTokenFrom(response)) {
      if (typeof setToken === "function") setToken("");
      console.warn("Wallet availability returned no rotated token; wallets hidden");
      return NO_WALLET_AVAILABILITY;
    }
    rotate(response);
    return normalizeWalletAvailability(response?.data);
  } catch (error) {
    rotate(error?.response);
    // No rotated token on a failed call: the one in the store may be spent.
    if (!rotatedTokenFrom(error?.response) && typeof setToken === "function") {
      setToken("");
    }
    console.warn("Wallet availability could not be loaded; wallets hidden");
    return NO_WALLET_AVAILABILITY;
  }
}

// A wallet is shown only when the paypoint has it enabled (init flag) AND the
// current host is enabled for it (availability).
export function resolveAllowedWallets(initData, availability) {
  return {
    applePay:
      initData?.applePay === true && availability?.applePay?.enabled === true,
    googlePay:
      initData?.googlePay === true && availability?.googlePay?.enabled === true,
  };
}
